import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import debounce from "lodash.debounce";

const Image = (props) => {
  const [size, setSize] = useState(null);
  const { image } = props;
  const imageRef = useRef();
  const lazyLoadRef = useRef();
  const placeholderRef = useRef();
  let io;
  let observer;

  useEffect(() => {
    window.addEventListener("resize", debounce(onResize), 500);
    if (image && image.id) {
      setImageObservable();
    }

    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [image]);

  useEffect(() => {
    loadBackgroundImage();
  }, [size]);

  const onResize = () => {
    // reinit intersection observer
    if (image && image.id) {
      if (!props.loadInstantly) {
        if (observer) {
          observer.disconnect();
        }
        setImageObservable();
      } else {
        loadImageInstantly();
      }
    }
  };

  const loadImageInstantly = () => {
    if (imageRef.current) {
      const element = imageRef.current;
      const width = element ? element.getBoundingClientRect().width : 1024;
      setSize(imageSizeForWidth(width));
    }
  };

  const paddingFromRatio = () => {
    const { ratio } = props;
    const wh = ratio ? ratio.split(":") : [16, 9];
    return ((100 / Number(wh[0])) * Number(wh[1])).toFixed(2).toString();
  };

  const setImageObservable = () => {
    io = new IntersectionObserver(
      (entries, observer) => {
        const entry = entries[0];
        observer = observer;

        if (entry && entry.isIntersecting) {
          setSize(imageSizeForWidth(entry.rootBounds.width));
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
      }
    );

    if (imageRef.current) io.observe(imageRef.current);
  };

  const loadBackgroundImage = () => {
    const { debug, background } = props;

    if (size && background) {
      var img = document.createElement("img");
      img.onload = (e) => {
        if (debug) console.log("lazy image loaded");
        if(lazyLoadRef.current) {
          lazyLoadRef.current.setAttribute(
              "style",
              `background-image: url(${size.url})`
          );
          placeholderRef.current.className = "placeholder hidden";
        }
      };
      if (debug) console.log("loading lazy image");
      img.src = size.url;
    }
  };

  const imageSizeForWidth = (containerWidth) => {
    const { debug } = props;

    let usedSizeName = "full";
    const usedSize = Object.keys(image.sizes).reduce((acc, sizeName) => {
      const size = image.sizes[sizeName];
      if (size.width >= containerWidth && size.width < acc.width) {
        usedSizeName = sizeName;
        acc = size;
      }
      return acc;
    }, image.sizes.full);

    if (debug)
      console.log(
        "for container width " + containerWidth + " using size:",
        usedSizeName,
        usedSize
      );

    return usedSize;
  };

  const {
    backgroundPosition,
    backgroundSize,
    className,
    onClick,
    background,
    children,
  } = props;

  if (!image) {
    console.warn('"image" prop is required for Image componenet');
    return null;
  }

  return (
    <Wrapper
      ref={imageRef}
      background={background}
      padding={paddingFromRatio()}
      backgroundPosition={backgroundPosition}
      backgroundSize={backgroundSize}
      className={className}
      onClick={onClick}
    >
      {background && size && (
        <React.Fragment>
          <div className="lazyload" ref={lazyLoadRef} title={image.title}>
            <span>{image.alt}</span>
            {children}
          </div>
          <div
            className="placeholder"
            ref={placeholderRef}
            style={{ backgroundImage: `url(${image.sizes.min.url})` }}
          />
        </React.Fragment>
      )}
      {!background && size && (
        <img src={size.url} alt={image.alt} title={image.title} />
      )}
    </Wrapper>
  );
};

export default Image;

const Wrapper = styled.div`
  position: relative;
  ${(props) =>
    props.background
      ? `
        padding-top: ${props.padding}%;
        overflow:hidden;
        width:100%;
    `
      : `
    
    `}

  > div {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background-size: ${(props) =>
      props.backgroundSize ? props.backgroundSize : "cover"};
    background-repeat: no-repeat;
    background-position: ${(props) =>
      props.backgroundPosition ? props.backgroundPosition : `center center`};
    background-color: transparent !important;
    &.placeholder {
      transition: none;
      filter: blur(10px);
      top: -5px;
      left: -5px;
      bottom: -5px;
      right: -5px;
      opacity: 1;
      transition: all 0.2s ease-in-out;
      &:hover {
        transform: none;
      }
      &.hidden {
        opacity: 0;
      }
    }

    span {
      color: transparent;
    }
  }

  img {
  }
`;
